import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import NotoSansDevanagariRegular from '../../assets/fonts/NotoSansDevanagari-Regular.ttf';
import NotoSansDevanagariBold from '../../assets/fonts/NotoSansDevanagari-Bold.ttf';
import { SECTION_CUSTOM_FIELDS_KEY } from '../../constants/sectionCustomFields';

Font.register({
  family: 'NotoSansDevanagari',
  fonts: [
    { src: NotoSansDevanagariRegular, fontWeight: 'normal' },
    { src: NotoSansDevanagariBold, fontWeight: 'bold' },
  ],
});

const PERSONAL_SECTION_TITLES = ['वैयक्तिक माहिती', 'ज्योतिष', 'शिक्षण आणि काम'];
const FAMILY_SECTION_TITLE = 'कौटुंबिक माहिती';
const CONTACT_SECTION_TITLE = 'संपर्क';

const baseStyles = StyleSheet.create({
  page: { fontFamily: 'NotoSansDevanagari', position: 'relative' },
  imageContainer: { position: 'relative', width: '100%', height: '100%' },
  backgroundImage: { width: '100%', height: '100%', opacity: 1.0 },
  content: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: 80, paddingTop: 10 },
  additionalImageContainer: { position: 'absolute', top: 150, right: 45, width: 180, height: 180, justifyContent: 'center', alignItems: 'center' },
  additionalImage: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 10 },
  imagePreviewContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 3, marginTop: 25 },
  imagePreview: { width: 60, height: 60 },
});

const createTypographyStyles = (contentFontSize) => {
  const fs = Math.max(4, Number(contentFontSize) || 15);
  const headerFs = Math.min(18, fs + 2);
  return StyleSheet.create({
    sectionHeader: { fontSize: headerFs, fontWeight: 'bold', textAlign: 'center', marginTop: 1, marginBottom: 1, color: '#5C2D6E' },
    line: { flexDirection: 'row', alignItems: 'flex-start' },
    label: { fontSize: fs, width: '26%' },
    value: { fontSize: fs, width: '70%', marginLeft: '10px' },
    colon: { fontSize: fs, marginRight: 3 },
  });
};

const PDFDocument = ({
  formData,
  templateId,
  additionalImage,
  imagePreview,
  centerText,
  contentFontSize = 15,
}) => {
  const fs = Math.max(4, Number(contentFontSize) || 15);
  const typo = createTypographyStyles(fs);
  const contentPad = Math.max(44, 78 - (15 - fs) * 5);

  const getBackgroundImage = () => {
    const id = String(templateId);
    switch (id) {
      case "1": return { src: "Marriage Biodata Templatep-01.png", style: { top: 180, right: 50, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
      case "2": return { src: "Marriage Biodata Templatep-02.png", style: { top: 180, right: 50, borderWidth: 2, borderColor: '#a15740', borderRadius: 10 } };
      case "3": return { src: "Marriage Biodata Templatep-03.png", style: { top: 180, right: 50, borderWidth: 2, borderColor: '#cebfac', borderRadius: 10 } };
      case "4": return { src: "Marriage Biodata Templatep-04.png", style: { top: 180, right: 52, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
      case "5": return { src: "Marriage Biodata Templatep-05.png", style: { top: 180, right: 40, borderWidth: 2, borderColor: '#B83D52', borderRadius: 10 } };
      case "6": return { src: "Marriage Biodata Templatep-06.png", style: { top: 180, right: 40, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
      case "7": return { src: "Marriage Biodata Templatep-07.png", style: { top: 185, right: 60, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
      case "8": return { src: "Marriage Biodata Templatep-08.png", style: { top: 180, right: 40, borderWidth: 2, borderColor: '#5C2D6E', borderRadius: 10 } };
      case "9": return { src: "Marriage Biodata Templatep-09.png", style: { top: 185, right: 52, borderWidth: 2, borderColor: '#6B5E65', borderRadius: 10 } };
      case "10": return { src: "Marriage Biodata Templatep-10.png", style: { top: 180, right: 40, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
      case "11": return { src: "Marriage Biodata Templatep-11.png", style: { top: 210, right: 40, borderWidth: 2, borderColor: '#e8bcc0', borderRadius: 10 } };
      case "12": return { src: "Marriage Biodata Templatep-12.png", style: { top: 180, right: 40, borderWidth: 2, borderColor: '#c682aa', borderRadius: 10 } };
      default: return { src: "Marriage Biodata Templatep-01.png", style: { top: 180, right: 50, borderWidth: 2, borderColor: '#C9A86C', borderRadius: 10 } };
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    const { day, month, year, varr } = dateObj;
    return `${varr} ${day}-${month}-${year}`;
  };

  const formatTime = (timeObj) => {
    if (!timeObj) return '';
    const { hour, minute, period } = timeObj;
    return `${period} ${hour} वा. ${minute} मि.`;
  };

  const formatValue = (value, key) => {
    switch (key) {
      case 'जन्मतारीख': return formatDate(value);
      case 'जन्मवेळ': return formatTime(value);
      case 'बहीण':
      case 'भाऊ':
      case 'मामा':
      case 'चूलते':
      case 'दाजी':
        return value.some(item => item.value) ? value.map(item => item.value).join(', ') : '';
      default: return value && value.value ? value.value : '';
    }
  };

  const formatLabel = (label) => String(label).replace(/_/g, ' ');

  const sectionCustom = formData?.[SECTION_CUSTOM_FIELDS_KEY] || {};

  const renderCustomRows = (sectionTitle) => {
    const rows = sectionCustom[sectionTitle];
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.map((row) => {
      const t = (row.title || '').trim();
      const v = (row.value || '').trim();
      if (!t && !v) return null;
      const id = row.id || `${sectionTitle}-${t}-${v}`;
      return (
        <View key={id} style={typo.line}>
          <Text style={typo.label}>{t || '—'}</Text>
          <Text style={typo.colon}>: </Text>
          <Text style={typo.value}>{v}</Text>
        </View>
      );
    });
  };

  const { src: backgroundImage, style: additionalImageStyle } = getBackgroundImage();

  return (
    <Document>
      <Page size="A4" style={baseStyles.page}>
        <View style={baseStyles.imageContainer}>
          <Image style={baseStyles.backgroundImage} src={backgroundImage} />
          <View style={{ ...baseStyles.content, padding: contentPad, paddingTop: Math.max(6, contentPad - 70) }}>
            <View style={baseStyles.imagePreviewContainer}>
              <Image style={baseStyles.imagePreview} src={imagePreview} />
            </View>
            <Text style={typo.sectionHeader}>{centerText}</Text>
            <Text style={typo.sectionHeader}>बायोडाटा</Text>
            {additionalImage && (
              <View style={{ ...baseStyles.additionalImageContainer, ...additionalImageStyle }}>
                <Image style={baseStyles.additionalImage} src={additionalImage} />
              </View>
            )}
            {Object.entries(formData).map(([key, value]) =>
              key !== SECTION_CUSTOM_FIELDS_KEY &&
              !['वडिलांचे_नाव', 'वडिलांचा_व्यवसाय', 'आईचे_नाव', 'बहीण', 'भाऊ', 'मामा', 'चूलते', 'दाजी', 'पत्ता', 'मोबाईल_नं', 'नातेसंबंध', 'इतर'].includes(key) && formatValue(value, key) ? (
                <View key={key} style={typo.line}>
                  <Text style={typo.label}>{formatLabel(value.title || key)}</Text>
                  <Text style={typo.colon}>: </Text>
                  <Text style={typo.value}>{formatValue(value, key)}</Text>
                </View>
              ) : null
            )}
            {PERSONAL_SECTION_TITLES.map((st) => renderCustomRows(st))}
            <Text style={typo.sectionHeader}>कौटुंबिक माहिती</Text>
            {Object.entries(formData).map(([key, value]) =>
              key !== SECTION_CUSTOM_FIELDS_KEY &&
              ['वडिलांचे_नाव', 'वडिलांचा_व्यवसाय', 'आईचे_नाव', 'बहीण', 'भाऊ', 'मामा', 'चूलते', 'दाजी', 'नातेसंबंध', 'इतर'].includes(key) && formatValue(value, key) ? (
                <View key={key} style={typo.line}>
                  <Text style={typo.label}>{formatLabel(value.title || key)}</Text>
                  <Text style={typo.colon}>: </Text>
                  <Text style={typo.value}>{formatValue(value, key)}</Text>
                </View>
              ) : null
            )}
            {renderCustomRows(FAMILY_SECTION_TITLE)}
            <Text style={typo.sectionHeader}>संपर्क</Text>
            {Object.entries(formData).map(([key, value]) =>
              key !== SECTION_CUSTOM_FIELDS_KEY &&
              ['पत्ता', 'मोबाईल_नं'].includes(key) && formatValue(value, key) ? (
                <View key={key} style={typo.line}>
                  <Text style={typo.label}>{formatLabel(value.title || key)}</Text>
                  <Text style={typo.colon}>: </Text>
                  <Text style={typo.value}>{formatValue(value, key)}</Text>
                </View>
              ) : null
            )}
            {renderCustomRows(CONTACT_SECTION_TITLE)}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;
